import { convertMoneyToNumber } from '../util/currency';

// Analytics handlers are created once in src/index.js. Components and thunks
// send events through here instead of reaching into the Redux middleware,
// which only knows about page views.
//
// Until the handlers are registered every call is a no-op, so this is safe to
// call during server-side rendering.
let handlers = [];

export const setAnalyticsHandlers = analyticsHandlers => {
  handlers = analyticsHandlers || [];
};

/**
 * Send an event to every configured analytics handler.
 *
 * Note: event params end up in the browser and are sent to Google, so they must
 * not contain personal data (names, emails, addresses, message contents).
 *
 * @param {String} event GA4 event name, e.g. 'view_item'
 * @param {Object} params GA4 event parameters
 */
export const trackEvent = (event, params = {}) => {
  // Analytics must never break the flow it is measuring. Some of these calls sit
  // in the checkout success path, where a throw would be swallowed by the
  // surrounding .catch and the buyer would see a failure after a successful payment.
  try {
    handlers.forEach(handler => {
      if (typeof handler.trackEvent === 'function') {
        handler.trackEvent(event, params);
      }
    });
  } catch (e) {
    console.error('Analytics event failed:', event, e);
  }
};

// convertMoneyToNumber throws on anything that isn't a Money in a supported
// currency. These mappers are evaluated as arguments to trackEvent, so a throw
// here would escape its try/catch - in the checkout success path that would
// surface a payment taken as a payment failed. Drop the amount instead.
const toAmount = (price, key) => {
  if (!price) {
    return {};
  }
  try {
    return { [key]: convertMoneyToNumber(price) };
  } catch (e) {
    console.error('Analytics could not read price:', e);
    return {};
  }
};

/**
 * Map a listing entity to a GA4 ecommerce item.
 * Only public listing data is included - see the note on trackEvent.
 *
 * @param {Object} listing listing entity
 * @param {Object} extra additional GA4 item fields, e.g. { index: 0 }
 * @returns {Object} GA4 item
 */
export const listingToItem = (listing, extra = {}) => {
  return {
    item_id: listing?.id?.uuid,
    item_name: listing?.attributes?.title,
    ...toAmount(listing?.attributes?.price, 'price'),
    ...extra,
  };
};

/**
 * GA4 expects a plain number and a currency code alongside ecommerce items.
 *
 * @param {Money} price Money object (amount in subunits)
 * @returns {Object} object-literal with currency and value, or empty if no price
 */
export const priceParams = price => {
  const value = toAmount(price, 'value');
  return value.value == null ? {} : { currency: price.currency, ...value };
};
